import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  listarShares,
  crearShare,
  actualizarShare,
  revocarShare,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, Mail } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "Pendiente", variant: "secondary" },
  accepted: { label: "Aceptada", variant: "default" },
  rejected: { label: "Rechazada", variant: "destructive" },
  revoked: { label: "Revocada", variant: "outline" },
};

export default function CompartirContent({ empresaId }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [permitirEscritura, setPermitirEscritura] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadShares = async () => {
    try {
      const res = await listarShares(empresaId);
      setShares(res.data || []);
    } catch (e) {
      toast.error("Error cargando invitaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Introduce un email");
      return;
    }
    setSubmitting(true);
    try {
      await crearShare(empresaId, {
        email: email.trim(),
        permitir_escritura: permitirEscritura,
      });
      toast.success("Invitación enviada");
      setEmail("");
      setPermitirEscritura(false);
      loadShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al invitar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePermission = async (share) => {
    const nuevoPermitir = share.permission !== "write";
    try {
      await actualizarShare(empresaId, share.id, { permitir_escritura: nuevoPermitir });
      toast.success("Permiso actualizado");
      loadShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleRevoke = async (share) => {
    try {
      await revocarShare(empresaId, share.id);
      toast.success("Acceso revocado");
      loadShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al revocar");
    }
  };

  const activeShares = shares.filter((s) => s.status === "pending" || s.status === "accepted");
  const closedShares = shares.filter((s) => s.status === "rejected" || s.status === "revoked");

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Compartir empresa
          </CardTitle>
          <CardDescription>
            Invita a otro usuario por email. Por defecto tendrá acceso de solo lectura.
            Marca la casilla para permitirle también editar. Solo tú podrás borrar la empresa.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_auto] items-end">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email del destinatario</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="ej: socio@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="share-email-input"
                />
              </div>
              <Button type="submit" disabled={submitting} data-testid="share-submit">
                <Mail className="h-4 w-4 mr-2" />
                {submitting ? "Enviando…" : "Invitar"}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="permitir-escritura"
                checked={permitirEscritura}
                onCheckedChange={(v) => setPermitirEscritura(!!v)}
                data-testid="share-write-checkbox"
              />
              <Label htmlFor="permitir-escritura" className="cursor-pointer font-normal">
                Permitir escritura (editar ejercicios y datos)
              </Label>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invitaciones activas</CardTitle>
          <CardDescription>Pendientes y aceptadas</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Cargando…</div>
          ) : activeShares.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">Sin invitaciones activas</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Permiso</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeShares.map((s) => (
                  <TableRow key={s.id} data-testid={`share-row-${s.id}`}>
                    <TableCell className="font-medium">{s.invited_email}</TableCell>
                    <TableCell>
                      <Badge variant={s.permission === "write" ? "default" : "outline"}>
                        {s.permission === "write" ? "Lectura + escritura" : "Solo lectura"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_LABEL[s.status]?.variant || "secondary"}>
                        {STATUS_LABEL[s.status]?.label || s.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTogglePermission(s)}
                          data-testid={`share-toggle-${s.id}`}
                        >
                          {s.permission === "write" ? "Quitar escritura" : "Permitir escritura"}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`share-revoke-${s.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Revocar acceso?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {s.invited_email} dejará de tener acceso a esta empresa de forma inmediata.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRevoke(s)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Revocar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {closedShares.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-muted-foreground">
              Histórico ({closedShares.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Permiso</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Cerrada</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {closedShares.map((s) => (
                  <TableRow key={s.id} className="text-muted-foreground">
                    <TableCell>{s.invited_email}</TableCell>
                    <TableCell>{s.permission}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_LABEL[s.status]?.variant || "secondary"}>
                        {STATUS_LABEL[s.status]?.label || s.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.responded_at ? new Date(s.responded_at).toLocaleDateString("es-ES") : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
